import { Web } from 'sip.js';
import { buildAudioConstraintLadder, hasVirtualDefault } from './webrtc';
import { reportMediaAcquisitionError } from './reportMediaError';
import log from './log';

/**
 * Errors a retry cannot help with. Permission was refused for the ORIGIN, not for a device, so
 * every rung of the ladder fails the same way - and re-asking would put a second permission prompt
 * in front of a ringing call.
 */
const TERMINAL_ERROR_NAMES = ['NotAllowedError', 'SecurityError'];

type AudioConstraint = MediaStreamConstraints['audio'];
type AudioConstraintObject = Exclude<AudioConstraint, boolean | undefined>;

function isTerminalError(err: any): boolean {
  return TERMINAL_ERROR_NAMES.includes(err?.name);
}

/** The exact deviceId a constraint requires, or null when it does not require one. */
function requiredDeviceId(audio: AudioConstraint): string | null {
  if (typeof audio !== 'object' || audio === null) {
    return null;
  }

  const { deviceId } = audio;

  if (
    typeof deviceId === 'object' &&
    deviceId !== null &&
    'exact' in deviceId
  ) {
    const { exact } = deviceId as { exact?: string | string[] };

    return typeof exact === 'string' ? exact : null;
  }

  return null;
}

/**
 * Everything the caller asked for EXCEPT the device selection (echoCancellation and friends), so a
 * fallback rung relaxes which microphone is used without silently dropping how it is processed.
 * `undefined` when there is nothing to carry over.
 */
function audioProcessingConstraints(
  audio: AudioConstraint
): AudioConstraintObject | undefined {
  if (typeof audio !== 'object' || audio === null) {
    return undefined;
  }

  const processing: AudioConstraintObject = { ...audio };

  delete processing.deviceId;

  return Object.keys(processing).length > 0 ? processing : undefined;
}

/**
 * @name buildLadder
 * @description The constraints to try for this acquisition, in order. The ordering itself comes
 * from `buildAudioConstraintLadder`, shared with the pre-call probe; this only re-applies any
 * processing constraints the caller asked for on top of each rung.
 */
function buildLadder(
  audio: AudioConstraint,
  aggregateAvailable: boolean
): AudioConstraint[] {
  const deviceId = requiredDeviceId(audio);
  const rungs: AudioConstraint[] = buildAudioConstraintLadder(
    deviceId ? { deviceId: { exact: deviceId } } : true,
    aggregateAvailable
  );
  const processing = audioProcessingConstraints(audio);

  if (!processing) {
    return rungs;
  }

  return rungs.map(rung =>
    rung === true
      ? { ...processing }
      : { ...processing, ...(rung as AudioConstraintObject) }
  );
}

/**
 * @name createRetryingMediaStreamFactory
 * @description A sip.js `MediaStreamFactory` that falls back through relaxed audio constraints
 * instead of failing the session.
 *
 * This is where the cost of requiring an exact device is paid off. sip.js acquires media from
 * inside `.invite()` / `.accept()`, and a rejection there answers 480 and terminates the session -
 * with no possible retry, because `.accept()` refuses a second call once the session has left the
 * Initial state. Wrapping the acquisition rather than guarding it from outside means a recoverable
 * failure is resolved BEFORE sip.js ever sees an error, so an unplugged headset costs the preferred
 * device and not the call. It also covers the re-INVITEs behind hold/unhold, which no pre-call
 * check reaches.
 *
 * The ladder is walked only after the constraints as asked have failed, so the happy path stays at
 * exactly one `getUserMedia` and zero enumerations. Permission failures are rethrown immediately:
 * see `TERMINAL_ERROR_NAMES`.
 *
 * A recovered acquisition is reported to Sentry under the existing `recovered: true` fingerprint -
 * the user's chosen microphone is unusable and that is worth knowing - and `onFallback` lets the
 * store re-resolve the device manager, so the pickers name the device that actually opened rather
 * than the one that lost.
 * @param {() => void} [onFallback] - called once when a rung other than the first one opened.
 * @returns {Web.MediaStreamFactory}
 */
export function createRetryingMediaStreamFactory(
  onFallback?: () => void
): Web.MediaStreamFactory {
  return async (constraints: MediaStreamConstraints) => {
    // Both guards mirror sip.js's `defaultMediaStreamFactory`, which this replaces wholesale.
    if (!constraints.audio && !constraints.video) {
      return new MediaStream();
    }

    if (navigator.mediaDevices === undefined) {
      throw new Error('Media devices not available in insecure contexts.');
    }

    let firstError: any;

    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      if (isTerminalError(err)) {
        throw err;
      }

      firstError = err;
    }

    let aggregateAvailable = false;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      aggregateAvailable = hasVirtualDefault(devices, 'audioinput');
    } catch (err) {
      // Nothing to learn, so the aggregate rung is skipped and the ladder ends at `true`.
      log.error('Failed to enumerate devices while falling back.', err);
    }

    const fallbacks = buildLadder(constraints.audio, aggregateAvailable).slice(
      1
    );

    log.warn(
      `NUACOM-LOG: the requested microphone could not be opened (${firstError?.name}), trying ${fallbacks.length} fallback(s).`,
      firstError
    );

    let lastError = firstError;

    for (let i = 0; i < fallbacks.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const stream = await navigator.mediaDevices.getUserMedia({
          ...constraints,
          audio: fallbacks[i],
        });

        reportMediaAcquisitionError(firstError, {
          direction: 'unknown',
          recovered: true,
          deviceId: requiredDeviceId(constraints.audio),
          constraint: firstError?.constraint ?? null,
          // The rung that opened: the last one is always the unconstrained request.
          attempt: i + 1,
        });

        if (onFallback) {
          onFallback();
        }

        return stream;
      } catch (err) {
        if (isTerminalError(err)) {
          throw err;
        }

        lastError = err;
      }
    }

    // Every rung failed, including the unconstrained one: there is no usable microphone. sip.js
    // now behaves exactly as it did before this factory existed.
    throw lastError;
  };
}

export default createRetryingMediaStreamFactory;
